import { type ReactNode, useEffect } from "react";
import { MapContainer, TileLayer, useMapEvents } from "react-leaflet";
import type { LeafletMouseEvent } from "leaflet";
import { ensureLeafletIconsPatched } from "@/components/maps/leafletIcons";
import { cn } from "@/utils/cn";

function ClickCapture({ onClick }: { onClick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e: LeafletMouseEvent) {
      onClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

/**
 * Base map surface (OpenStreetMap tiles, no API key required) used for the
 * Live Rides operations map and the Zones service-area map. Compose with
 * <MapMarker> children for pins/polylines. Pass `onMapClick` to capture
 * clicks (used by the zone polygon draw tool).
 */
export function Map({
  center,
  zoom = 13,
  height = 360,
  onMapClick,
  children,
  className,
}: {
  center: [number, number];
  zoom?: number;
  height?: number | string;
  onMapClick?: (lat: number, lng: number) => void;
  children?: ReactNode;
  className?: string;
}) {
  useEffect(() => {
    ensureLeafletIconsPatched();
  }, []);

  return (
    <div style={{ height }} className={cn("relative isolate z-0", className)}>
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
          // OSM rejects tile requests that carry no Referer (403 "Access blocked").
          referrerPolicy="strict-origin-when-cross-origin"
        />
        {onMapClick && <ClickCapture onClick={onMapClick} />}
        {children}
      </MapContainer>
    </div>
  );
}
